import { ClickAwayListener, IconButton } from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import { Collection, Linked } from '@type-r/models';
import { pureRenderProps, useLinked, useModelCopy } from '@type-r/react';
import { asCurrency, Changes, CollapsableSection, FormValue, InlineTable, Input, ReadOnly, RowActions, RowEditActions, TableCell, TableHeader, TableRow, TableTotals } from 'lqx-react';
import * as React from 'react';
import { FunctionComponent, useContext } from 'react';
import { PartModel } from 'api';

const emptyPart = new PartModel();

export const ComponentsSection =
    pureRenderProps({
        parts : Collection.of( PartModel ),
        originalParts : Collection.of( PartModel ),
        disableAdd: false,
        disableEdit: false,
        disableRemove: false,
        disableQuantityCheck: false,
        allowZeroQuantity: false
    },
    ({ parts, originalParts, disableAdd, disableEdit, disableRemove, disableQuantityCheck, allowZeroQuantity }) => {
        const readOnly = useContext( ReadOnly ),
            total = parts.reduce( ( acc: number, b ) => acc + b.extended_price, 0 ),
            prevTotal = originalParts.reduce( ( acc: number, b ) => acc + b.extended_price, 0 );
        const showCollapsed = !parts || !parts.length;
        return (
            <CollapsableSection
                id="components"
                title="Components"
                right={
                    !readOnly && !disableAdd ?
                        <IconButton aria-label="Add" size="small" onClick={ e => {
                            e.stopPropagation();
                            parts.add({assignedIdentification: parts.models[parts.models.length - 1].attributes.assignedIdentification + 1});
                        }}>
                            <AddCircleIcon />
                        </IconButton>
                    : null
                }
                notExpanded={showCollapsed}
            >
                <InlineTable noBorders
                    head={
                        <TableHeader>
                            <TableCell size="small" />
                            <TableCell>Unit/Part #</TableCell>
                            <TableCell align="right">Assigned ID</TableCell>
                            <TableCell align="right">QTY</TableCell>
                            <TableCell align="right">Unit Cost</TableCell>
                            <TableCell align="right">Extended Price</TableCell>
                        </TableHeader>
                    }
                >
                    { parts.map( row  => (
                        <Row
                            key={ row.cid }
                            original={ originalParts.get( row.id ) || emptyPart }
                            model={ row }
                            readOnly={ readOnly }
                            disableEdit={ disableEdit }
                            disableRemove={ disableRemove }
                            disableQuantityCheck = { disableQuantityCheck }
                            allowZeroQuantity = { allowZeroQuantity }/>
                    ))}
                    <TableTotals>
                        <TableCell/>
                        <TableCell align="left">
                            Total
                        </TableCell>
                        <TableCell/><TableCell/><TableCell/>
                        <TableCell align="right">
                            <Changes prev={prevTotal} formatter={asCurrency}>
                                { total }
                            </Changes>
                        </TableCell>
                    </TableTotals>
                </InlineTable>
            </CollapsableSection>
        );
    }) as FunctionComponent<{ parts: Collection<PartModel>, originalParts: Collection<PartModel>, disableAdd?: boolean, disableEdit?: boolean, disableRemove?: boolean, disableQuantityCheck?: boolean, allowZeroQuantity?: boolean }>;



const Row = ({ model, original, readOnly, disableQuantityCheck, disableEdit, disableRemove, allowZeroQuantity } : {
    model : PartModel,
    original : PartModel,
    readOnly : boolean,
    disableQuantityCheck : boolean,
    disableEdit : boolean,
    disableRemove : boolean,
    allowZeroQuantity: boolean
}) => {
    const $editing = useLinked( false ),
        edit = () => readOnly || $editing.true(),
        remove = () => model.collection.remove( model );

    const changed = original.partNumber !== model.partNumber ||
        original.quantity !== model.quantity ||
        original.price !== model.price;

    return <>
        { $editing.value || !model.partNumber /* (adding part) */ ?
            <EditRow model={ model }
                     original={ original }
                     $editing={ $editing }
                     disableQuantityCheck = { disableQuantityCheck }
                     allowZeroQuantity = { allowZeroQuantity }/>
        :
            <TableRow onClick={ edit }>
                <RowActions onEdit={ !disableEdit && edit } onDelete={ !disableRemove && remove } />
                <TableCell>
                    <Changes prev={original.partNumber}>
                        {model.partNumber}
                    </Changes>
                </TableCell>
                <TableCell align="right">{model.assignedIdentification}</TableCell>
                <TableCell align="right">
                    <Changes prev={original.quantity}>
                        {model.quantity}
                    </Changes>
                </TableCell>
                <TableCell align="right">
                    <Changes prev={original.price} formatter={asCurrency}>
                        { model.price }
                    </Changes>
                </TableCell>
                <TableCell align="right">
                    <Changes prev={original.extended_price} formatter={asCurrency}>
                        {model.extended_price}
                    </Changes>
                </TableCell>
            </TableRow>
        }
    </>
}

const EditRow = ({ model, original, disableQuantityCheck, allowZeroQuantity, $editing }:{
    model : PartModel,
    original : PartModel,
    disableQuantityCheck : boolean,
    allowZeroQuantity: boolean,
    $editing : Linked<boolean>
}) => {
    const local = useModelCopy( model );
    // TODO: rename @linked useSyncLinked to useLinkedCopy, which would accept the link only and clone underlying object.

    const $quantity = local.$.quantity.check(q => disableQuantityCheck || q <= original.quantity)
                                      .check(q => allowZeroQuantity || q > 0);

    function save(){
        if( local.isValid() && !$quantity.error) {
            model.assignFrom( local );
        }
        else if( local.isNew() ){
            model.collection.remove( model );
        }

        $editing.set( false );
    }

    console.log( local.partNumber.length, model.partNumber.length )

    return (
        <ClickAwayListener onClickAway={ save }>
            <TableRow>
                <RowEditActions
                    onSave={ save }
                    onCancel={ () => {
                        if( model.isNew() ){
                            model.collection.remove( model );
                        }

                        $editing.set( false )
                    }}
                />
                <TableCell className="xlarge">
                    <Input $value={local.$.partNumber} />
                </TableCell>
                <TableCell align="right" className="small">
                    <FormValue>{ local.assignedIdentification }</FormValue>
                </TableCell>

                <TableCell align="right" className="small">
                    <Input positive integer $value={ $quantity } />
                </TableCell>

                <TableCell align="right" className="medium">
                    <Input positive number $value={ local.$.price } />
                </TableCell>

                <TableCell align="right" className="medium">
                    <FormValue>{ asCurrency( local.extended_price ) }</FormValue>
                </TableCell>
            </TableRow>
        </ClickAwayListener>
    )
}
